import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../AuthContext";
import { Switch } from "@mui/material"; // Import MUI Switch
import { toast } from 'react-toastify';
import "./OrganizationsList.css";
import configData from '../../configure';

const OrganizationsList = ({ currentOrgId, onOrgIdSelect }) => {
  const { axios, token, isValidated, user } = useAuth();

  const [organizations, setOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [parentOrgId, setParentOrgId] = useState(null);

  // Modal states (shared for add & edit)
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [formData, setFormData] = useState({
    orgName: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "Telangana",
    country: "India",
    email: "",
    phone: "",
    imageUrl: "  ",
    orgStatus: "ACTIVE",
  });
  const [modalLoading, setModalLoading] = useState(false);

  const fetchOrganizations = useCallback(async () => {
    if (!token || !isValidated || !user?.orgId) return;
    setIsLoading(true);

    try {
      // 1. Get detailed org info to find its parentOrgId
      const orgResponse = await axios.get(
        configData.get_identity_identity_org_orgid(user.orgId, user.orgId),
        { headers: { accept: "application/hal+json" } }
      );

      const pId = orgResponse.data.parentOrgId;
      setParentOrgId(pId);

      if (pId) {
        // 2. Fetch all organizations under that parent
        const response = await axios.get(
          configData.get_identity_identity_org_parentorg(pId),
          { headers: { accept: "application/hal+json" } }
        );
        setOrganizations(response.data || []);
      } else {
        // Fallback: if no parent, just show the current org
        setOrganizations([orgResponse.data]);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to load organizations");
    } finally {
      setIsLoading(false);
    }
  }, [token, isValidated, user?.orgId, axios]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Prepare form for ADD or EDIT
  const openModal = (mode = "add", org = null) => {
    setIsEditMode(mode === "edit");
    setEditingOrg(org);

    if (mode === "edit" && org) {
      const addr = org.addressDetails || {};
      const contact = org.contact || {};
      setFormData({
        orgName: org.orgName || "",
        addressLine1: addr.addressLine1 || "",
        addressLine2: addr.addressLine2 || "",
        city: addr.city || "",
        state: addr.state || "Telangana",
        country: addr.country || "India",
        email: contact.email || "",
        phone: contact.phone || "",
        imageUrl: (org.imageUrl || "  ").trim(),
        orgStatus: org.orgStatus || "ACTIVE",
        upiId: org.customFields?.upiId || org.customFields?.upi_id || org.upiId || "",
      });
    } else {
      // Reset for ADD
      setFormData({
        orgName: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "Telangana",
        country: "India",
        email: "",
        phone: "",
        imageUrl: "  ",
        orgStatus: "ACTIVE",
        upiId: "",
      });
    }

    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalLoading(true);

    const updatedCustomFields = {
      ...(editingOrg?.customFields || {}),
      upiId: formData.upiId ? formData.upiId.trim() : null,
    };

    const payload = {
      createdBy: null,
      createdAt: null,
      lastUpdatedBy: null,
      lastUpdatedAt: null,
      orgName: formData.orgName.trim(),
      parentOrgId: parentOrgId,
      addressDetails: {
        addressType: null,
        addressLine1: formData.addressLine1 || null,
        addressLine2: formData.addressLine2 || null,
        city: formData.city || null,
        state: formData.state || null,
        country: formData.country || null,
        pinCode: null,
        alternatePhoneNumber: null,
      },
      contact: {
        email: formData.email || null,
        phone: formData.phone || null,
      },
      imageUrl: formData.imageUrl.trim() || "  ",
      orgStatus: formData.orgStatus,
      customFields: updatedCustomFields,
      upiId: formData.upiId ? formData.upiId.trim() : null,
    };

    try {
      let response;

      if (isEditMode && editingOrg) {
        // EDIT - PUT request
        payload.orgId = editingOrg.orgId;
        response = await axios.put(
          configData.get_identity_identity_org(editingOrg.orgId),
          payload,
          {
            headers: {
              accept: "application/hal+json",
              "Content-Type": "application/json",
            },
          }
        );
        toast.success(`Organization "${formData.orgName}" updated successfully`);
      } else {
        // ADD - POST request
        response = await axios.post(configData.IDENTITY_ORG, payload, {
          headers: {
            accept: "application/hal+json",
            "Content-Type": "application/json",
          },
        });
        toast.success(`New organization created (ID: ${response.data.orgId})`);
      }

      setShowModal(false);
      fetchOrganizations();
    } catch (err) {
      toast.error(err.response?.data?.message || (isEditMode ? "Update failed" : "Creation failed"));
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (orgId) => {
    try {
      await axios.delete(configData.get_identity_identity_org(orgId), {
        headers: { accept: "*/*" },
      });
      toast.success("Organization deleted successfully");
      fetchOrganizations();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleSwitchOrg = (orgId, orgName) => {
    if (onOrgIdSelect) {
      onOrgIdSelect(orgId);
      toast.success(`Switched to "${orgName}". All data will now load for this organization.`);
    }
  };

  const getCityState = (org) => {
    const addr = org.addressDetails;
    if (!addr) return "—";
    return [addr.city, addr.state].filter(Boolean).join(", ") || "—";
  };

  return (
    <div className="organizations-list">
      <div className="list-header">
        <h2>Organizations</h2>
        <button
          className="add-org-btn"
          onClick={() => openModal("add")}
          disabled={!token || !isValidated}
        >
          + Add Organization
        </button>
      </div>

      {currentOrgId && (
        <div className="current-org-banner">
          <span className="banner-icon">🏢</span>
          <span className="banner-text">
            <strong>Active Organization:</strong>{" "}
            {organizations.find((o) => o.orgId === currentOrgId)?.orgName || currentOrgId}
          </span>
        </div>
      )}

      {isLoading && <p className="loading">Loading organizations...</p>}

      {organizations.length > 0 && !isLoading && (
        <table className="org-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Location</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Switch</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {organizations.map((org) => {
              const isCurrentOrg = org.orgId === currentOrgId;
              return (
                <tr key={org.orgId} className={isCurrentOrg ? "current-org-row" : ""}>
                  <td>
                    {org.orgName || "Unnamed"}
                    {isCurrentOrg && <span className="current-org-indicator">✓</span>}
                  </td>
                  <td>{getCityState(org)}</td>
                  <td>{org.contact?.email || "—"}</td>
                  <td>{org.contact?.phone || "—"}</td>
                  <td className="switch-cell">
                    <div className="switch-wrapper">
                      <Switch
                        checked={isCurrentOrg}
                        onChange={() => !isCurrentOrg && handleSwitchOrg(org.orgId, org.orgName)}
                        color="primary"
                        disabled={isCurrentOrg}
                        size="small"
                        inputProps={{ "aria-label": "switch organization" }}
                      />
                    </div>
                  </td>
                  <td className="actions-cell">
                    <div className="actions-wrapper">
                      <button
                        className="edit-btn"
                        onClick={() => openModal("edit", org)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => setConfirmDelete({ orgId: org.orgId, orgName: org.orgName })}
                        title="Delete"
                        disabled={isCurrentOrg}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )
      }

      {
        organizations.length === 0 && !isLoading && (
          <p>No organizations found under this parent.</p>
        )
      }

      {/* Shared Modal for Add / Edit */}
      {
        showModal && (
          <div className="modal-overlay">
            <div className="add-modal">
              <h3>{isEditMode ? "Edit Organization" : "Add New Organization"}</h3>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Organization Name *</label>
                  <input
                    type="text"
                    name="orgName"
                    value={formData.orgName}
                    onChange={handleFormChange}
                    required
                  />
                </div>
                <div className="form-row">

                  <div className="form-group">
                    <label>Address Line 1</label>
                    <input
                      type="text"
                      name="addressLine1"
                      value={formData.addressLine1}
                      onChange={handleFormChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Address Line 2</label>
                    <input
                      type="text"
                      name="addressLine2"
                      value={formData.addressLine2}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group ">
                    <label>City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group ">
                    <label>State</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group ">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group ">
                    <label>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>UPI ID</label>
                  <input
                    type="text"
                    name="upiId"
                    value={formData.upiId}
                    onChange={handleFormChange}
                    placeholder="e.g. schoolname@upi"
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select name="orgStatus" value={formData.orgStatus} onChange={handleFormChange}>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => setShowModal(false)}
                    disabled={modalLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="save-btn"
                    disabled={modalLoading || !formData.orgName.trim()}
                  >
                    {modalLoading
                      ? isEditMode
                        ? "Updating..."
                        : "Creating..."
                      : isEditMode
                        ? "Save Changes"
                        : "Create Organization"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        confirmDelete && (
          <div className="confirm-modal-overlay">
            <div className="confirm-modal">
              <h3>Confirm Delete</h3>
              <p>
                Are you sure you want to delete <strong>{confirmDelete.orgName}</strong>?
              </p>
              <div className="modal-buttons">
                <button className="cancel-btn" onClick={() => setConfirmDelete(null)}>
                  Cancel
                </button>
                <button
                  className="confirm-delete-btn"
                  onClick={() => handleDelete(confirmDelete.orgId)}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default OrganizationsList;